import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Printer, Receipt } from "lucide-react";
import { toast } from "sonner";
import { inr, fmtDT, openPdfPath } from "./wardUtils";

const today = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function Stat({ label, value, tid, warn }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular ${warn ? "text-amber-600" : ""}`} data-testid={tid}>{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-border overflow-x-auto">
      <div className="px-5 py-3 bg-muted/40 font-medium">{title}</div>
      <table className="w-full text-sm">{children}</table>
    </section>
  );
}

const TH = ({ children, right }) => <th className={`px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground ${right ? "text-right" : "text-left"}`}>{children}</th>;

export default function WardReport() {
  const [date, setDate] = useState(today());
  const [r, setR] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!date) return;
    api.get("/ward/reports/daily", { params: { date } }).then(({ data }) => setR(data)).catch(() => toast.error("Could not load report"));
  }, [date]);

  const t = r?.totals;
  return (
    <div className="space-y-6" data-testid="ward-report-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav("/ward")} data-testid="ward-report-back"><ArrowLeft size={18} /></Button>
          <div>
            <h1 className="font-display text-2xl font-semibold">Daily Collection Report</h1>
            <p className="text-sm text-muted-foreground">Payments collected on the selected day and dues of every admitted patient.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="ward-report-date" />
          <Button variant="outline" onClick={() => openPdfPath(`/ward/reports/daily/pdf?date=${date}`)} data-testid="ward-report-print"><Printer size={15} className="mr-1" /> Print</Button>
        </div>
      </div>

      {!r ? <div className="text-muted-foreground py-10 text-center">Loading…</div> : (<>
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <Stat label="Collected" value={inr(t.collected)} tid="ward-report-collected" />
          <Stat label="Payments" value={t.count} tid="ward-report-count" />
          <Stat label="Admitted patients" value={t.admitted_count} tid="ward-report-admitted-count" />
          <Stat label="Outstanding (admitted)" value={inr(t.outstanding)} tid="ward-report-outstanding" warn={t.outstanding > 0} />
        </div>
        {Object.keys(r.by_method).length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="ward-report-by-method">
            {Object.entries(r.by_method).map(([m, v]) => <Badge key={m} variant="secondary">{m.toUpperCase()} · {inr(v)}</Badge>)}
          </div>
        )}

        <Section title="Collections">
          <thead><tr><TH>Time</TH><TH>Receipt</TH><TH>Patient</TH><TH>Mode</TH><TH>Reference</TH><TH right>Amount</TH><TH /></tr></thead>
          <tbody>
            {r.collections.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground" data-testid="ward-report-no-collections">No payments collected on this day.</td></tr>}
            {r.collections.map((c) => (
              <tr key={`${c.bill_id}-${c.payment_id}`} className="border-t border-border" data-testid={`ward-report-row-${c.receipt_no}`}>
                <td className="px-4 py-2 text-muted-foreground">{fmtDT(c.at)}</td>
                <td className="px-4 py-2 font-mono text-xs">{c.receipt_no}</td>
                <td className="px-4 py-2 cursor-pointer hover:underline" onClick={() => nav(`/ward/bills/${c.bill_id}`)}>{c.patient_name}</td>
                <td className="px-4 py-2">{c.method.toUpperCase()}</td>
                <td className="px-4 py-2 text-muted-foreground">{c.reference || "—"}</td>
                <td className="px-4 py-2 text-right tabular font-medium">{inr(c.amount)}</td>
                <td className="px-2 text-right">{c.payment_id && <Button variant="ghost" size="icon" title="Print receipt" onClick={() => openPdfPath(`/ward/bills/${c.bill_id}/payments/${c.payment_id}/receipt`)} data-testid={`ward-report-receipt-${c.receipt_no}`}><Receipt size={15} /></Button>}</td>
              </tr>
            ))}
          </tbody>
        </Section>

        <Section title="Outstanding — currently admitted">
          <thead><tr><TH>Bill</TH><TH>Patient</TH><TH>Ward / Bed</TH><TH>Admitted</TH><TH right>Net</TH><TH right>Paid</TH><TH right>Balance</TH></tr></thead>
          <tbody>
            {r.admitted.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground" data-testid="ward-report-no-admitted">No patients currently admitted.</td></tr>}
            {r.admitted.map((a) => (
              <tr key={a.bill_id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => nav(`/ward/bills/${a.bill_id}`)} data-testid={`ward-report-admitted-${a.bill_no}`}>
                <td className="px-4 py-2 font-mono text-xs">{a.bill_no}</td>
                <td className="px-4 py-2 font-medium">{a.patient_name}</td>
                <td className="px-4 py-2">{a.ward}{a.bed_number ? ` / ${a.bed_number}` : ""}</td>
                <td className="px-4 py-2 text-muted-foreground">{fmtDT(a.admission_at)}</td>
                <td className="px-4 py-2 text-right tabular">{inr(a.net_payable)}</td>
                <td className="px-4 py-2 text-right tabular">{inr(a.paid_total)}</td>
                <td className="px-4 py-2 text-right"><Badge variant={a.balance_due > 0 ? "destructive" : "secondary"}>{inr(a.balance_due)}</Badge></td>
              </tr>
            ))}
          </tbody>
        </Section>
      </>)}
    </div>
  );
}
